import { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Loader2, Check, X, Info } from "lucide-react";
import { api } from "@/lib/api";
import { SourceBadge } from "@/components/SourceBadge";

const STATUS = {
  healthy: { Icon: ShieldCheck, cls: "text-[#2E7D46]", ring: "border-[#2E7D46]/30 bg-[#2E7D46]/5" },
  attention: { Icon: AlertTriangle, cls: "text-amber-600", ring: "border-amber-500/30 bg-amber-500/5" },
  building: { Icon: Info, cls: "text-charcoal/50", ring: "border-saddle/25 bg-white" },
};

function fmt(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return null; }
}

// HomeRange Verified — transparent per-state data-health panel (real data only).
export function DataHealthPanel({ state }) {
  const [d, setD] = useState(null);
  const [legend, setLegend] = useState(false);

  useEffect(() => {
    let alive = true;
    setD(null);
    api.stateDataHealth(state).then((r) => { if (alive) setD(r); }).catch(() => { if (alive) setD(false); });
    return () => { alive = false; };
  }, [state]);

  if (d === false) return null;
  if (!d) return (
    <section className="max-w-[1400px] mx-auto px-5 sm:px-10" data-testid="data-health-loading">
      <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
    </section>
  );

  const s = STATUS[d.status] || STATUS.building;
  const StatusIcon = s.Icon;
  const verified = fmt(d.last_verified);

  return (
    <section className="max-w-[1400px] mx-auto px-5 sm:px-10" data-testid="data-health-panel">
      <div className={`rounded-lg border ${s.ring} p-5 sm:p-6`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className={`shrink-0 w-10 h-10 rounded-full grid place-items-center border ${s.cls} border-current/30`}>
              <StatusIcon size={18} />
            </span>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="hr-overline text-[11px] text-charcoal/50">HomeRange Verified</span>
                <SourceBadge tier="official" size="xs" />
              </div>
              <p className={`font-serif text-xl sm:text-2xl text-charcoal mt-0.5`} data-testid="data-health-headline">
                {d.headline}
              </p>
              <p className="text-charcoal/55 text-sm mt-1">
                {d.official_source_count} official source{d.official_source_count === 1 ? "" : "s"} on file
                {verified ? ` · last checked ${verified}` : ""}
              </p>
            </div>
          </div>
          <button
            onClick={() => setLegend((v) => !v)}
            className="text-xs font-semibold text-blaze hover:underline inline-flex items-center gap-1 shrink-0"
            data-testid="data-health-legend-toggle"
          >
            <Info size={13} /> {legend ? "Hide" : "What do these mean?"}
          </button>
        </div>

        {/* Checks */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mt-5">
          {d.checks.map((c) => (
            <div key={c.key} className="bg-white/70 border border-saddle/15 rounded-md px-3 py-2.5 flex items-start gap-2.5" data-testid={`data-health-check-${c.key}`}>
              <span className={`shrink-0 w-5 h-5 rounded-full grid place-items-center mt-0.5 ${c.ok ? "bg-[#2E7D46]/15 text-[#2E7D46]" : "bg-charcoal/10 text-charcoal/40"}`}>
                {c.ok ? <Check size={12} /> : <X size={12} />}
              </span>
              <div className="min-w-0">
                <p className="text-charcoal text-[13px] font-semibold leading-tight">{c.label}</p>
                <p className="text-charcoal/50 text-[11px] mt-0.5">{c.detail}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust legend */}
        {legend && (
          <div className="mt-5 pt-4 border-t border-saddle/15 space-y-2" data-testid="data-health-legend">
            {(d.tiers || []).map((t) => (
              <div key={t.key} className="flex items-start gap-3">
                <SourceBadge tier={t.key} size="xs" className="shrink-0 mt-0.5" />
                <p className="text-charcoal/60 text-xs leading-snug">{t.desc}</p>
              </div>
            ))}
          </div>
        )}

        <p className="text-charcoal/45 text-[11px] mt-4 flex items-start gap-1.5" data-testid="data-health-disclaimer">
          <Info size={12} className="mt-0.5 shrink-0" /> {d.disclaimer}
        </p>
      </div>
    </section>
  );
}
